<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useToastStore } from '../stores/toast';

const toast = useToastStore();

/** Lot BI : secondes restantes du délai d'« Annuler », recalculées 4 fois par seconde. */
const now = ref(Date.now());
let ticker: ReturnType<typeof setInterval> | undefined;
watch(
  () => toast.countdown,
  (countdown) => {
    clearInterval(ticker);
    now.value = Date.now();
    if (countdown) ticker = setInterval(() => (now.value = Date.now()), 250);
  },
);
const remaining = computed(() =>
  toast.countdown ? Math.max(0, Math.ceil((toast.countdown.endsAt - now.value) / 1000)) : 0,
);
const totalSeconds = computed(() => (toast.countdown ? Math.round(toast.countdown.totalMs / 1000) : 0));

function onAction(): void {
  const action = toast.action;
  toast.hide();
  action?.run();
}

function editable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
}

/** Ctrl+Z (⌘Z sur Mac) annule aussi, hors d'un champ de saisie (où il garde son sens habituel). */
function onKeydown(event: KeyboardEvent): void {
  if (!toast.visible || !toast.action) return;
  if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return;
  if (event.key.toLowerCase() !== 'z' || editable(event.target)) return;
  event.preventDefault();
  onAction();
}

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  clearInterval(ticker);
});
</script>

<template>
  <div
    id="toast"
    role="status"
    aria-live="polite"
    :class="{ on: toast.visible, linked: !!toast.link || !!toast.action }"
  >
    {{ toast.message
    }}<RouterLink
      v-if="toast.link"
      class="toast-link"
      :to="toast.link.to"
      data-testid="toast-link"
      @click="toast.hide()"
      >{{ toast.link.label }}</RouterLink
    ><template v-if="toast.action"
      ><span class="toast-sep" aria-hidden="true"> · </span
      ><button
        type="button"
        class="toast-action"
        data-testid="toast-undo"
        aria-keyshortcuts="Control+Z Meta+Z"
        @click="onAction"
      >
        {{ toast.action.label }}</button
      ><span v-if="toast.countdown" class="toast-count" aria-hidden="true" data-testid="toast-count"
        >{{ remaining }} s</span
      ><span v-if="toast.countdown" class="sr-only"
        >Annulation possible pendant {{ totalSeconds }} secondes (Ctrl+Z).</span
      ><span
        v-if="toast.countdown"
        :key="toast.countdown.endsAt"
        class="toast-bar"
        aria-hidden="true"
        :style="{ animationDuration: `${toast.countdown.totalMs}ms` }"
      ></span
    ></template>
  </div>
</template>

<style scoped>
/* Lot T : un toast avec un lien reçoit les clics (le toast simple les laisse passer). Lot BI : aussi « Annuler ». */
#toast.on.linked {
  pointer-events: auto;
}
.toast-link {
  margin-left: 10px;
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
}
.toast-action {
  margin-left: 2px;
  padding: 2px 6px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}
.toast-action:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 1px;
}
.toast-count {
  margin-left: 8px;
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}
.toast-bar {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 3px;
  height: 2px;
  border-radius: 1px;
  background: currentColor;
  opacity: 0.55;
  transform-origin: left center;
  animation-name: toast-countdown;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
@keyframes toast-countdown {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .toast-bar {
    display: none;
  }
}
</style>
